<script setup lang="ts">
import { useQuery } from "@vue/apollo-composable";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { OgReportRequestModalQuery } from "@/__generated__/types";
import AtButton from "@/components/atoms/AtButton/AtButton.vue";
import AtCheckbox from "@/components/atoms/AtCheckbox/AtCheckbox.vue";
import MlModal from "@/components/molecules/MlModal.vue";
import MlSelect from "@/components/molecules/MlSelect/MlSelect.vue";
import OgDateRangeInput from "@/components/organisms/OgDateRangeInput.vue";
import useCurrentUser from "@/utils/composables/useCurrentUser/useCurrentUser";
import { useReportingPeriod } from "@/utils/composables/useReportingPeriod/useReportingPeriod";
import OG_REPORT_REQUEST_MODEL_QUERY from "./OgReportRequestModel.query";

interface TProps {
	isRevealed: boolean;
	title?: string;
	description?: string;
	allowPdfExport?: boolean;
	allowWordExport?: boolean;
	allowXlsxExport?: boolean;
	allowPdfBetaExport?: boolean;
}

const props = withDefaults(defineProps<TProps>(), {
	title: "",
	description: "",
	allowXlsxExport: true,
	allowPdfExport: true,
	allowWordExport: false,
	allowPdfBetaExport: false,
});
const emit = defineEmits(["update:isRevealed", "closeModal", "submit"]);

const { t } = useI18n();

const { currentUser } = useCurrentUser();

const { result } = useQuery<OgReportRequestModalQuery>(
	OG_REPORT_REQUEST_MODEL_QUERY,
);

const {
	dateRange: allWorkspacesReportingPeriodDateRange,
	formatForBE: allWorkspacesReportingPeriodFormatForBE,
} = useReportingPeriod();

const workspaceOptions = computed(
	() =>
		currentUser.value?.entities.reduce<Record<string, string>>(
			(acc, { _id, name }) => ({ ...acc, [_id]: name ?? "" }),
			{},
		) ?? {},
);

const locationOptions = computed(
	() =>
		result?.value?.getProjectsAdmin.reduce<Record<string, string>>(
			(acc, { _id, name }) => ({ ...acc, [_id]: name ?? "" }),
			{},
		) ?? {},
);

const selectedWorkspaces = ref<string[]>();
const selectedLocations = ref<string[]>();

const selectedExportFormat = ref("xlsx");
</script>

<template>
  <MlModal
    :isRevealed="props.isRevealed"
    :class="{ 'bg-blue-50': true }"
    @close="emit('closeModal')"
    @update:isRevealed="emit('update:isRevealed', $event)"
  >
    <div>
      <h3 class="font-bold text-lg mb-6">
        {{ t('Data export options') }}
      </h3>

      <p class="text-sm">{{t('In which data format would you like the data to be exported?')}}</p>

      <div class="my-6">
        <div class="flex items-center" v-if="props.allowWordExport">
          <AtCheckbox
              :checked="selectedExportFormat === 'word'"
              @toggleCheckbox="selectedExportFormat = 'word'"
          />
          <span
              class="text-gray-700 text-xs font-medium"
          >{{ t('Microsoft Word') }}</span>
        </div>
        <div class="flex items-center" v-if="props.allowPdfBetaExport">
          <AtCheckbox
              :checked="selectedExportFormat === 'pdf_beta'"
              @toggleCheckbox="selectedExportFormat = 'pdf_beta'"
          />
          <span
              class="text-gray-700 text-xs font-medium"
          >{{ t('PDF (Project Export)') }}</span>
        </div>

        <div class="flex items-center" v-if="props.allowPdfExport">
          <AtCheckbox
              :checked="selectedExportFormat === 'pdf'"
              @toggleCheckbox="selectedExportFormat = 'pdf'"
          />
          <span
              class="text-gray-700 text-xs font-medium"
          >{{ t('PDF (Organization Export)') }}</span>
        </div>

        <div class="flex items-center" v-if="props.allowXlsxExport">
          <AtCheckbox
              :checked="selectedExportFormat === 'xlsx'"
              @toggleCheckbox="selectedExportFormat = 'xlsx'"
          />
          <span
              class="text-gray-700 text-xs font-medium"
          >{{ t('Microsoft Excel') }}</span>
        </div>
      </div>


      <p class="text-sm mb-2">{{t('Choose the time frame for data export:')}}</p>

      <OgDateRangeInput
          v-model="allWorkspacesReportingPeriodDateRange"
          class="mb-10"
      />

      <template v-if="!['word', 'pdf_beta'].includes(selectedExportFormat)">
        <p class="text-sm mb-2">{{t('Which workspaces should be exported?')}}</p>

        <MlSelect
            v-model="selectedWorkspaces"
            wrapperClass="w-64"
            title=""
            :multiple="true"
            :options="workspaceOptions"
        />
      </template>
        <template v-else>
          <p class="text-sm mb-2">{{t('Which projects should be exported?')}}</p>

          <MlSelect
              v-model="selectedLocations"
              wrapperClass="w-64"
              :multiple="true"
              title=""
              :options="locationOptions"
          />

        </template>


      <div class="mt-6 flex justify-between">
        <AtButton
          class="mr-2"
          variant="outline"
          @click.stop="emit('closeModal')"
        >
          {{ t('Cancel') }}
        </AtButton>
        <AtButton
          class="ml-auto"
          @click.stop="emit('submit', { type: selectedExportFormat, dateRange: allWorkspacesReportingPeriodFormatForBE(), workspaces: selectedWorkspaces, locations: selectedLocations })"
        >
          {{t('Download')}}
        </AtButton>
      </div>
    </div>
  </MlModal>
</template>
